/* eslint "@tanstack/query/exhaustive-deps": "off" */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { AppleWallet } from '@eika-infrastruktur/mobilbank-lib/src/nativeProps';

import { getApplePayEligibleCards } from '@/react-query/services/card';

import { ApplePayEligibleCard, ApplePayEligibleCardsRequestParameters } from '@generated-types';
import hashFromString from '@utils/hashFromString';

export const ELIGIBLE_CARDS_QUERY_KEY = 'apple-pay-eligible-cards';

const generatePayload = (appleWallet: AppleWallet | undefined): ApplePayEligibleCardsRequestParameters => ({
    localPassList: appleWallet?.localPasses ?? [],
    passes: [],
    remotePassList: appleWallet?.remotePasses ?? [],
    watchPaired: appleWallet?.isWatchPaired ?? false,
});

const useApplePayEligibleCardQuery = (
    appleWallet: AppleWallet | undefined,
    operationAllowed = false,
    enabled = true,
) => {
    const queryClient = useQueryClient();
    const [localPassQueryKey, setLocalPassQueryKey] = useState<string | undefined>();
    const [remotePassQueryKey, setRemotePassQueryKey] = useState<string | undefined>();
    const [watchPairedQueryKey, setWatchPairedQueryKey] = useState<string | undefined>();

    useEffect(() => {
        hashFromString(appleWallet?.localPasses?.map((pass) => pass.value).join() ?? '').then(setLocalPassQueryKey);
    }, [appleWallet?.localPasses]);

    useEffect(() => {
        hashFromString(appleWallet?.remotePasses?.map((pass) => pass.value).join() ?? '').then(setRemotePassQueryKey);
    }, [appleWallet?.remotePasses]);

    useEffect(() => {
        hashFromString(appleWallet?.isWatchPaired ? 'paired' : 'not-paired').then(setWatchPairedQueryKey);
    }, [appleWallet?.isWatchPaired]);

    const queryKey = [ELIGIBLE_CARDS_QUERY_KEY, localPassQueryKey, remotePassQueryKey, watchPairedQueryKey];

    const query = useQuery<ApplePayEligibleCard[]>({
        queryKey,
        queryFn: () => getApplePayEligibleCards(generatePayload(appleWallet)),
        enabled:
            operationAllowed &&
            enabled &&
            Boolean(localPassQueryKey) &&
            Boolean(remotePassQueryKey) &&
            Boolean(watchPairedQueryKey),
    });

    // eslint-disable-next-line @tanstack/query/no-rest-destructuring
    return { ...query, invalidateQuery: () => queryClient.invalidateQueries({ queryKey: [ELIGIBLE_CARDS_QUERY_KEY] }) };
};

export default useApplePayEligibleCardQuery;
